import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import RequireAuth from "../../Components/Common/RequireAuth";
import Loader from "../../Components/Loader/Loader";
import RouteLoader from "../../Components/Loader/RouteLoader";

// Login/OTP + static pages load eagerly (they're the entry paths).
import Login from "../../Components/Authentication/Login";
import OTP from "../../Components/Authentication/OTP";
import OTPVerify from "../../Components/Authentication/VerifyOTP";
import ChangePassword from "../../Components/Authentication/ChangePassword";
import ForgotPassword from "../../Components/Authentication/ForgotPassword";
import ResetPassword from "../../Components/Authentication/ResetPassword";
import AuthCallback from "../../Components/Authentication/AuthCallback";
import SocketContext from "../../Components/Authentication/SocketContext";
import TiktokRedirect from "../../Components/Authentication/Tiktokredirect";
import TransferTokenOTP from "../../Components/Authentication/TransferTokenOTP";
import PrivacyPolicies from "./PrivacyPolicies";
import TermsandConditions from "./TermsandConditions";
import Faq from "./Faq";
import UserLayout from "./UserLayout";
import NotFound from "./NotFound";


// Heavy / feature routes — code-split via React.lazy (S-28).
const Dashboard = lazy(() => import("./Dashboard"));
const Profile = lazy(() => import("./Profile"));
const Chatbot = lazy(() => import("./Chatbot"));
const Gmail = lazy(() => import("./Gmail"));
const Instagram = lazy(() => import("./Instagram"));
const InstagramPosts = lazy(() => import("./InstagramPosts"));
// const ChatAI = lazy(() => import("./ChatAI"));
const AllowInstaAccess = lazy(() =>import("../../Components/UserPanel/Instagram/AllowInstaAccess"));
const TransactionHistory = lazy(() =>import("../../Components/UserPanel/Instagram/TransactionHistory"));
const TikTokLogin = lazy(() => import("./Tiktoklogin"));
const TiktokUserProfile = lazy(() => import("./TiktokUserProfile"));
const Subscription = lazy(() => import("./Subscription"));
const PaymentForm = lazy(() => import("./PaymentForm"));
const SuccessPage = lazy(() => import("./SuccessPage"));
const FailedPage = lazy(() => import("./FailedPage"));
const Setting = lazy(() => import("./Setting"));
const GooglePhotos = lazy(() => import("./GooglePhotos"));
const ActivePlan = lazy(() => import("./ActivePlan"));
const UserTransactionHistory = lazy(() => import("./TransactionHistory"));
const WalletDetails = lazy(() => import("./WalletDetails"));
const MintingNFT = lazy(() => import("./MintingNFT"));
const GeminiPage = lazy(() => import("./GeminiPage"));
const TikTokPost = lazy(() => import("./TikTokPost"));
const GetAllRequestsPage = lazy(() =>import("./BlockchainRequestPages/GetAllRequestsPage"));
const Checkout = lazy(() => import("./CheckoutPage"));
const Plans = lazy(() => import("./Plans"));
const PlanHistory = lazy(() => import("./PlanHistory"));
const WeePointReward = lazy(() => import("./WeePointReward"));
const Referral = lazy(() => import("./WeePointsPages/Referral"));
const LeaderBoard = lazy(() => import("./WeePointsPages/LeaderBoard"));
const WeePointsHistory = lazy(() => import("./WeePointsPages/WeePointsHistory"));
const GoogleCalenderEvents = lazy(() => import("./GoogleCalenderEvents"));
const ListAllApplicationUser = lazy(() => import("./ListAllApplicationUser"));
const ListGoogleDraft = lazy(() => import("./ListGoogleDraft"));
const AICreditsTransactions = lazy(() =>import("./AICredits/AICreditsTransactions"));
const DreamCreateScreen = lazy(() => import("./DreamMachine/DreamCreateScreen"));
const BlueprintScreen = lazy(() => import("./DreamMachine/BlueprintScreen"));
const VowScreen = lazy(() => import("./DreamMachine/VowScreen"));
const GardenScreen = lazy(() => import("./DreamMachine/GardenScreen"));
const DreamDetailsScreen = lazy(() => import("./DreamMachine/DreamDetailsScreen"));
const GetALLUserBadges = lazy(() => import("./GetALLUserBadges"));
const PLMHub = lazy(() => import("./PLMHub/PLMHub"));
const GoogleDrive = lazy(() => import("./GoogleDrive/GoogleDrive"));

// Wrap an element in both RequireAuth (S-12) and Suspense fallback.
const guarded = (node) => <RequireAuth>{node}</RequireAuth>;

function AppRoutes() {
  return (
    <UserLayout>
      <RouteLoader />
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Login />} />
          <Route path="/otp" element={<OTP />} />
          <Route path="/verifyotp" element={<OTPVerify />} />
          <Route path="/changepassword" element={<ChangePassword />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/resetpassword" element={<ResetPassword />} />
          <Route path="/qrcode" element={<SocketContext />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/tiktokredirect" element={<TiktokRedirect />} />
          <Route path="/privacypolicy" element={<PrivacyPolicies />} />
          <Route path="/termsandconditions" element={<TermsandConditions />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/otp-consent" element={<TransferTokenOTP />} />

          {/* Protected */}
          <Route path="/dashboard" element={guarded(<Dashboard />)} />
          <Route path="/profile" element={guarded(<Profile />)} />
          <Route path="/instagramaccess" element={guarded(<AllowInstaAccess />)} />
          <Route path="/Chatbot" element={guarded(<Chatbot />)} />
          <Route path="/mail" element={guarded(<Gmail />)} />
          <Route path="/setting" element={guarded(<Setting />)} />
          <Route path="/googlephotos" element={guarded(<GooglePhotos />)} />
          <Route path="/Instagram" element={guarded(<Instagram />)} />
          <Route path="/transactionhistory" element={guarded(<TransactionHistory />)} />
          <Route path="/igposts" element={guarded(<InstagramPosts />)} />
          {/* <Route path="/Chatai" element={guarded(<ChatAI />)} /> */}
          <Route path="/tiktok" element={guarded(<TikTokLogin />)} />
          <Route path="/tiktokuserprofile" element={guarded(<TiktokUserProfile />)} />
          <Route path="/Subscription" element={guarded(<Subscription />)} />
          <Route path="/activeplan" element={guarded(<ActivePlan />)} />
          <Route path="/payment" element={guarded(<PaymentForm />)} />
          <Route path="/success" element={guarded(<SuccessPage />)} />
          <Route path="/failed" element={guarded(<FailedPage />)} />
          <Route path="/transaction" element={guarded(<UserTransactionHistory />)} />
          <Route path="/walletdetails" element={guarded(<WalletDetails />)} />
          <Route path="/nftmint" element={guarded(<MintingNFT />)} />
          <Route path="/gemini" element={guarded(<GeminiPage />)} />
          <Route path="/tiktokpost" element={guarded(<TikTokPost />)} />
          <Route path="/getrequests" element={guarded(<GetAllRequestsPage />)} />
          <Route path="/checkout" element={guarded(<Checkout />)} />
          <Route path="/plans" element={guarded(<Plans />)} />
          <Route path="/planhistory" element={guarded(<PlanHistory />)} />
          <Route path="/weepoint-rewards" element={guarded(<WeePointReward />)} />
          <Route path="/referral" element={guarded(<Referral />)} />
          <Route path="/leaderboard" element={guarded(<LeaderBoard />)} />
          <Route path="/weepointshistory" element={guarded(<WeePointsHistory />)} />
          <Route path="/googleevents" element={guarded(<GoogleCalenderEvents />)} />
          <Route path="/listalluser" element={guarded(<ListAllApplicationUser />)} />
          <Route path="/draft" element={guarded(<ListGoogleDraft />)} />
          <Route path="/credittransactions" element={guarded(<AICreditsTransactions />)} />
          <Route path="/getallbadges" element={guarded(<GetALLUserBadges />)} />
          <Route path="/plm-hub" element={guarded(<PLMHub />)} />
          <Route path="/google-drive" element={guarded(<GoogleDrive />)} />

          {/* Dream Machine  */}
          {/* <Route path="/dream-machine/create" element={guarded(<DreamCreateScreen />)} />
          <Route path="/dream-machine/blueprint" element={guarded(<BlueprintScreen />)} />
          <Route path="/dream-machine/vow" element={guarded(<VowScreen />)} />
          <Route path="/dream-machine/garden" element={guarded(<GardenScreen />)} />
          <Route path="/dream-machine/details/:dreamId" element={guarded(<DreamDetailsScreen />)} /> */}

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </UserLayout>
  );
}

export default AppRoutes;
